/*
 * © 2017 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import {
  call,
  all,
  put,
  delay,
  select,
  race,
  take,
  getContext,
} from "typed-redux-saga";
import { PayloadAction } from "typesafe-actions";
import { data } from "./dataSaga";
import { RootState, rootActions } from "..";
import { getGlobalConfigs } from "@/index";
import { toUTCISOString } from "../../modules/dateFormats/index";
import { selectStartAndEnd, selectIsRollingMode } from "@/store/selectors";
import { CMSClientType } from "@/cms-api";
import { ProcessUpdatePresentation } from "@/presentation/processUpdates";
import { selectLastFetchedForRefresh } from "./selectors";
/**
 * Fetches all of the required static data for the current time range.
 */
export const allStaticData = function* () {
  try {
    yield* all([call(fetchStaticData)]);
  } catch (error) {
    console.log(error);
  }
};

export function* liveRefreshWatcher() {
  const config = yield* call(() => getGlobalConfigs());
  while (true) {
    // Loop every 1 second
    yield* delay(1000);

    const isRollingMode = yield* select(selectIsRollingMode);
    if (!isRollingMode) {
      continue;
    }

    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    if (!isLiveRefreshModeOn) {
      continue;
    }

    // Check if current date is after last fetched date + refresh rate
    const timeSinceLastFetchCompleted = yield* select(
      selectLastFetchedForRefresh
    );
    if (timeSinceLastFetchCompleted) {
      const now = new Date();
      const timeSinceLastFetch =
        now.getTime() - timeSinceLastFetchCompleted.getTime();

      if (timeSinceLastFetch < config.refreshRateFastInMs) {
        continue;
      }
    }
    yield* race({
      fetchDelta: call(fetchDeltas, config.maxTake),
      cancel: call(waitForLiveRefreshModeChanged),
    });
  }
}

function* waitForLiveRefreshModeChanged() {
  do {
    const isLiveRefreshModeOn = yield* select(
      (state: RootState) => state.global.isLiveRefreshModeOn
    );
    if (!isLiveRefreshModeOn) {
      return;
    }
  } while (yield* take(rootActions.global.liveRefreshModeStateChanged));
}

function* fetchStaticData() {
  const config = yield* call(() => getGlobalConfigs());
  const [from, to] = yield* select(selectStartAndEnd);
  try {
    yield* put(rootActions.processUpdates.fetchingStarted());
    yield* call(fetchOffsetAdjustment, from, to, config.maxTake, "static");
  } catch (error) {
    console.info(
      "something went wrong fetching static process updates data",
      error
    );
  } finally {
    yield* put(
      rootActions.processUpdates.fetchingFinished({
        to,
      })
    );
  }
}

function* fetchDeltas(take: number) {
  const now = toUTCISOString(new Date());
  try {
    const lastFetched = yield* select(
      (state: RootState) => state.processUpdates.lastFetched
    );
    const isRollingMode = yield* select(selectIsRollingMode);
    const isLiveRefreshModeOn = yield* select(
      (state) => state.global.isLiveRefreshModeOn
    );

    // comply with types
    if (!lastFetched || !isRollingMode || !isLiveRefreshModeOn) {
      return;
    }
    yield* put(rootActions.processUpdates.fetchingStarted());
    yield* call(fetchOffsetAdjustment, lastFetched, now, take, "delta");
  } catch (error) {
    console.log(error);
  } finally {
    yield* put(
      rootActions.processUpdates.fetchingFinished({
        to: now,
      })
    );
  }
}

function* fetchOffsetAdjustment(
  from: string,
  to: string,
  take: number,
  type: "static" | "delta"
) {
  try {
    const offsetAdjustments = yield* call(
      (from, to, take) =>
        data.fetchOffsetAdjustment({
          from,
          to,
          take,
        }),
      from,
      to,
      take
    );
    const payload = {
      events: offsetAdjustments,
      from: from,
      to: to,
    };
    if (type === "static") {
      yield* all([
        put(rootActions.processUpdates.processActionsFetched(payload)),
      ]);
    } else {
      yield* all([
        put(rootActions.processUpdates.processActionsDeltaFetched(payload)),
      ]);
    }
  } catch (error) {
    console.log(error);
  }
}

export function* getJobDetailsByIdSaga({
  payload: { processUpdate },
}: PayloadAction<
  "[process updates] details table inner table row clicked",
  { processUpdate: ProcessUpdatePresentation & { jobId: string } }
>) {
  const client: CMSClientType = yield getContext("client");
  const id = processUpdate.jobId;
  try {
    const job = yield* call(() => client.jobs.byId(id).promise);
    if (job)
      yield* put(
        rootActions.processUpdates.detailsTableInnerTableRowClickedRedirect({
          job,
          processUpdate,
        })
      );
    // load related jobs of focused job after redirect
    yield* put(
      rootActions.processUpdates.detailsTableInnerTableRowClickedSetJobDetails({
        job,
      })
    );
  } catch (e) {
    console.log("job not found");
  }
}
